import React from "react";
import { ChevronLeft, ChevronRight, Plus, Download, SlidersHorizontal, Truck, PackageCheck, Undo2 } from "lucide-react";
import { EVENT_STATUS, EVENT_CATEGORIES, catColor } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { STATUS_STYLE } from "@/components/calendar/calendarUtils";

const btn = "rounded-xl border border-zinc-800 bg-zinc-950 text-zinc-300 transition-colors hover:border-zinc-700 hover:text-white";

function Chip({ on, onClick, dot, label, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${on ? "border-[#FF6A00]/50 bg-[#FF6A00]/10 text-white" : "border-zinc-800 text-zinc-500 hover:text-zinc-300"}`}>
      <span className={`h-2 w-2 rounded-full ${dot} ${on ? "" : "opacity-40"}`} />{label}
    </button>
  );
}

function FilterMenu({ activeCats, toggleCat, activeStatuses, toggleStatus, resetFilters }) {
  const off = EVENT_CATEGORIES.length - activeCats.size + Object.keys(EVENT_STATUS).length - activeStatuses.size;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="cal-filter-btn" className={`flex h-9 items-center gap-2 px-3 text-sm ${btn}`}>
          <SlidersHorizontal className="h-4 w-4" />Filtrer
          {off > 0 && <span data-testid="cal-filter-count" className="flex h-5 min-w-5 items-center justify-center rounded-full bg-[#FF6A00] px-1 text-[10px] font-bold text-white">{off}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 space-y-4 border-zinc-800 bg-zinc-950 p-4 text-white" data-testid="cal-filter-panel">
        <div data-testid="category-filter">
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-zinc-500">Catégorie</p>
          <div className="flex flex-wrap gap-1.5">{EVENT_CATEGORIES.map((c) => <Chip key={c} testid={`cat-filter-${c}`} on={activeCats.has(c)} onClick={() => toggleCat(c)} dot={catColor(c).dot} label={c} />)}</div>
        </div>
        <div data-testid="status-filter">
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-zinc-500">Statut</p>
          <div className="flex flex-wrap gap-1.5">{Object.entries(EVENT_STATUS).map(([s, st]) => <Chip key={s} testid={`status-filter-${s}`} on={activeStatuses.has(s)} onClick={() => toggleStatus(s)} dot={st.dot} label={st.label} />)}</div>
        </div>
        {off > 0 && <button data-testid="cal-filter-reset" onClick={resetFilters} className="text-xs text-[#FF6A00] hover:underline">Tout réafficher</button>}
      </PopoverContent>
    </Popover>
  );
}

export function CalendarToolbar({ label, view, setView, onPrev, onNext, onToday, onExport, onNew, filters }) {
  return (
    <div className="flex flex-wrap items-center gap-2 p-4" data-testid="cal-toolbar">
      {view !== "list" && (
        <>
          <button data-testid="cal-prev" onClick={onPrev} className={`flex h-9 w-9 items-center justify-center ${btn}`}><ChevronLeft className="h-4 w-4" /></button>
          <button data-testid="cal-today" onClick={onToday} className={`h-9 min-w-[150px] px-4 text-sm font-medium ${btn}`} title="Revenir à aujourd'hui"><span data-testid="cal-range-label">{label}</span></button>
          <button data-testid="cal-next" onClick={onNext} className={`flex h-9 w-9 items-center justify-center ${btn}`}><ChevronRight className="h-4 w-4" /></button>
        </>
      )}
      <div className="ml-auto flex flex-wrap items-center gap-2">
        <FilterMenu {...filters} />
        <Select value={view} onValueChange={setView}>
          <SelectTrigger data-testid="cal-view-select" className={`h-9 w-[120px] ${btn}`}><SelectValue /></SelectTrigger>
          <SelectContent className="border-zinc-800 bg-zinc-950">
            <SelectItem data-testid="view-month" value="month">Mois</SelectItem>
            <SelectItem data-testid="view-week" value="week">Semaine</SelectItem>
            <SelectItem data-testid="view-list" value="list">Liste</SelectItem>
          </SelectContent>
        </Select>
        <button data-testid="export-csv-btn" onClick={onExport} title="Exporter CSV" className={`flex h-9 w-9 items-center justify-center ${btn}`}><Download className="h-4 w-4" /></button>
        <Button data-testid="new-event-btn" onClick={onNew} className="h-9 rounded-xl bg-[#FF6A00] font-semibold text-white shadow-lg shadow-[#FF6A00]/20 hover:bg-[#E65F00]"><Plus className="mr-1 h-4 w-4" />Événement</Button>
      </div>
    </div>
  );
}

export function StatusCounters({ counts, activeStatuses, onPick }) {
  const only = activeStatuses.size === 1 ? [...activeStatuses][0] : null;
  return (
    <div className="flex items-center gap-2 overflow-x-auto px-4 pb-4 sm:flex-wrap" data-testid="cal-status-counters">
      {Object.entries(EVENT_STATUS).map(([s, st]) => {
        const S = STATUS_STYLE[s];
        const n = counts[s] || 0;
        return (
          <button key={s} data-testid={`cal-count-${s}`} onClick={() => onPick(s)}
            className={`flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border px-2.5 text-sm transition-colors ${only === s ? "border-[#FF6A00] bg-[#FF6A00]/10" : "border-zinc-800 bg-zinc-950 hover:border-zinc-700"} ${n ? "" : "opacity-50"}`}>
            <S.icon className={`h-3.5 w-3.5 ${S.color}`} strokeWidth={2.2} />
            <span className="font-semibold text-white">{n}</span><span className="text-zinc-500">{st.label.toLowerCase()}</span>
          </button>
        );
      })}
      <div className="ml-auto hidden items-center gap-3 text-[11px] text-zinc-500 lg:flex" data-testid="cal-logi-legend">
        <span className="flex items-center gap-1"><Truck className="h-3 w-3 text-[#FF6A00]" />Chargement</span>
        <span className="flex items-center gap-1"><PackageCheck className="h-3 w-3 text-blue-400" />Livraison</span>
        <span className="flex items-center gap-1"><Undo2 className="h-3 w-3 text-emerald-400" />Récupération</span>
      </div>
    </div>
  );
}
